import { useCallback } from 'react';
import { PeopleAlt as PeopleAltIcon } from '@mui/icons-material';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { getPatientPrimaryContactPhone } from '@utils/patientUtils';

import {
  getSmsBrandPrefix,
  PATIENT_LINK_BASE_URL,
  SMS_COMPLIANCE_FOOTER,
  SMS_SENDER_PHONE,
} from '../../../components/patient-communication/constants';
import { PatientSendPanel } from '../../../components/patient-communication/PatientSendPanel';
import type {
  PreviewProps,
  SendData,
  SendResult,
} from '../../../components/patient-communication/types';
import type { Patient } from '../../../types/types';
import type {
  ProviderWaitingRoomNotifyRequest,
  ProviderWaitingRoomNotifyResponse,
} from '../waiting-room/types';

interface WaitingRoomInviteContent {
  label: string;
}

interface WaitingRoomInviteSendAdapterProps {
  open: boolean;
  onClose: () => void;
  encounterId?: string;
  patientId: string;
  patient?: Patient;
  waitingRoomUrl?: string | null;
  source?: string;
  notifyPatient: (
    request: ProviderWaitingRoomNotifyRequest,
  ) => Promise<ProviderWaitingRoomNotifyResponse>;
}

const FALLBACK_WAITING_ROOM_LINK = `${PATIENT_LINK_BASE_URL}/w/xxxxxxxx`;

export function WaitingRoomInviteSendAdapter({
  open,
  onClose,
  encounterId,
  patientId,
  patient,
  waitingRoomUrl,
  source = 'invite_dialog',
  notifyPatient,
}: WaitingRoomInviteSendAdapterProps) {
  const { telehealth } = useEventTracking();

  const content: WaitingRoomInviteContent[] = [{ label: 'Waiting room link' }];
  const previewUrl = waitingRoomUrl ?? FALLBACK_WAITING_ROOM_LINK;

  const prepareSendRequest = useCallback(
    (sendData: SendData): ProviderWaitingRoomNotifyRequest => ({
      patient_id: patientId,
      channels: sendData.channels,
      email: sendData.patient.email || undefined,
      mobile_phone: getPatientPrimaryContactPhone(sendData.patient) || undefined,
    }),
    [patientId],
  );

  const handleSend = useCallback(
    async (request: ProviderWaitingRoomNotifyRequest): Promise<SendResult> => {
      try {
        const response = await notifyPatient(request);

        if (response.success) {
          telehealth.patientNotified({
            encounter_id: encounterId,
            source,
          });
          return {
            success: true,
            channels: response.channels_successful,
            short_url: response.short_url,
          };
        }

        const errorMessage = Object.values(response.errors ?? {})
          .filter(Boolean)
          .join(', ');
        throw new Error(errorMessage || 'Failed to send waiting room link');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to send waiting room link';
        telehealth.sendToPatientFailed({
          encounter_id: encounterId,
          source,
          error_message: message,
        });
        throw error;
      }
    },
    [encounterId, notifyPatient, source, telehealth],
  );

  const contentRenderer = useCallback(
    (item: WaitingRoomInviteContent) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          bgcolor: 'grey.50',
          borderRadius: 1,
        }}
      >
        <PeopleAltIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {item.label}
        </Typography>
      </Box>
    ),
    [],
  );

  const previewRenderer = useCallback(
    (props: PreviewProps<WaitingRoomInviteContent>) => {
      const providerName = props.providerName || '[Provider Name]';

      const smsPreview = (
        <Box>
          <Typography variant="body2" component="div">
            {getSmsBrandPrefix(providerName)} You have been invited to join the telehealth waiting
            room:{' '}
            <Box component="span" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              {previewUrl}
            </Box>
            {props.customMessage && (
              <>
                <br />
                <br />
                {props.customMessage}
              </>
            )}
            <br />
            <br />
            {SMS_COMPLIANCE_FOOTER}
          </Typography>
          <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary' }}>
            Recipient will receive a text message from {SMS_SENDER_PHONE}
          </Typography>
        </Box>
      );

      const emailPreview = (
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a3055', mb: 2 }}>
            Join Telehealth Waiting Room
          </Typography>
          <Typography variant="body2" component="div">
            {providerName} invited you to join their telehealth waiting room.
          </Typography>
          <Box sx={{ textAlign: 'center', my: 3 }}>
            <Button
              variant="contained"
              disabled
              sx={{
                bgcolor: '#4169E1 !important',
                color: '#fff !important',
                textTransform: 'none',
                px: 4,
                py: 1.5,
                borderRadius: 1,
                fontSize: '1rem',
                '&.Mui-disabled': {
                  bgcolor: '#4169E1 !important',
                  color: '#fff !important',
                },
              }}
            >
              Join Waiting Room
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Or copy and paste this link into your browser:
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'primary.main', textDecoration: 'underline', mt: 0.5 }}
          >
            {previewUrl}
          </Typography>
          {props.customMessage && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              {props.customMessage}
            </Typography>
          )}
        </Box>
      );

      return (
        <Paper
          elevation={0}
          sx={{ py: 2.5, px: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
        >
          {props.deliveryMethod === 'both' ? (
            <>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                SMS Message
              </Typography>
              {smsPreview}
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Email Message
                </Typography>
                {emailPreview}
              </Box>
            </>
          ) : props.deliveryMethod === 'sms' ? (
            smsPreview
          ) : (
            emailPreview
          )}
        </Paper>
      );
    },
    [previewUrl],
  );

  return (
    <PatientSendPanel<WaitingRoomInviteContent, ProviderWaitingRoomNotifyRequest>
      open={open}
      onClose={onClose}
      patientId={patientId}
      patient={patient}
      variant="dialog"
      contentConfig={{
        title: 'Send Waiting Room Link',
        itemLabel: 'Invite',
        pluralLabel: 'Invites',
      }}
      content={content}
      contentRenderer={contentRenderer}
      previewRenderer={previewRenderer}
      defaultChannels={['sms']}
      allowedChannels={['sms', 'email']}
      prepareSendRequest={prepareSendRequest}
      onSend={handleSend}
    />
  );
}
